import { useEffect, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { ROTAS } from '@remoto/shared';
import { chamar } from '../api';
import { extensaoDe } from '../formato';

/** O que o painel sabe mostrar, decidido pela extensão do arquivo. */
export type Especie = 'markdown' | 'html' | 'pdf' | 'imagem' | 'audio' | 'video' | null;

const POR_EXTENSAO: Record<string, Especie> = {
  md: 'markdown',
  markdown: 'markdown',
  html: 'html',
  htm: 'html',
  pdf: 'pdf',
  png: 'imagem',
  jpg: 'imagem',
  jpeg: 'imagem',
  gif: 'imagem',
  webp: 'imagem',
  avif: 'imagem',
  bmp: 'imagem',
  mp3: 'audio',
  m4a: 'audio',
  wav: 'audio',
  ogg: 'audio',
  opus: 'audio',
  flac: 'audio',
  mp4: 'video',
  webm: 'video',
  mov: 'video',
  mkv: 'video',
};

export function especieDe(caminho: string): Especie {
  const extensao = extensaoDe(caminho);
  return POR_EXTENSAO[extensao] ?? null;
}

/**
 * Markdown renderizado.
 *
 * `rehype-sanitize` fica ligado mesmo o arquivo vindo de uma máquina nossa: o
 * conteúdo pode ter sido escrito pelo Claude a partir de algo que ele leu na
 * internet, e HTML cru dentro de um `.md` rodaria no nosso domínio, com a
 * sessão de quem está lendo.
 */
export function PreviewMarkdown({ texto }: { texto: string }): React.JSX.Element {
  // Sem teto de altura e sem rolagem própria: quem abre um `.md` no editor
  // quer a tela inteira, e o container de fora já rola. Os dois juntos davam
  // meia tela com barra dentro de barra.
  return (
    <div className="prose-remoto px-1">
      <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]}>
        {texto}
      </Markdown>
    </div>
  );
}

/**
 * Página HTML da máquina, dentro de uma moldura isolada.
 *
 * Este arquivo pode ter sido escrito pelo Claude a partir de qualquer coisa
 * que ele leu na internet. Rodar isso no nosso domínio entregaria a sessão de
 * quem está lendo — é o mesmo motivo pelo qual o `rehype-sanitize` fica ligado
 * no markdown, só que aqui não dá para sanear: uma página sem script deixaria
 * de ser a página.
 *
 * Por isso `sandbox` SEM `allow-same-origin`: a moldura ganha origem opaca, o
 * script dentro dela roda, mas não enxerga nosso cookie, nosso armazenamento
 * nem nosso DOM. As duas coisas juntas são o ponto — `allow-scripts` com
 * `allow-same-origin` no mesmo atributo devolve a moldura para a nossa origem
 * e desfaz a proteção inteira.
 *
 * `srcdoc` e não `src`: o conteúdo já está em memória, e servir por URL
 * exigiria uma rota nova com autenticação para uma coisa que é só leitura.
 */
export function PreviewHtml({ texto, nome }: { texto: string; nome: string }): React.JSX.Element {
  return (
    <iframe
      title={'Pré-visualização de ' + nome}
      srcDoc={texto}
      sandbox="allow-scripts"
      referrerPolicy="no-referrer"
      className="h-full w-full border-0 bg-white"
    />
  );
}

/**
 * Mídia da máquina.
 *
 * O endereço carrega um bilhete assinado de cinco minutos, porque `<video>` e
 * `<embed>` não sabem mandar cabeçalho de autenticação. Se o bilhete vence
 * enquanto a pessoa lê, um clique pede outro — vencer é o comportamento certo,
 * não um defeito a contornar com prazo longo.
 */
export function PreviewMidia({
  maquinaId,
  caminho,
  especie,
}: {
  maquinaId: string;
  caminho: string;
  especie: Exclude<Especie, 'markdown' | null>;
}): React.JSX.Element {
  const [url, setUrl] = useState('');
  const [erro, setErro] = useState('');
  const [tentativa, setTentativa] = useState(0);

  useEffect(() => {
    let vivo = true;
    setUrl('');
    setErro('');
    void chamar<{ token: string }>(`${ROTAS.maquinas}/${maquinaId}/arquivo/token`, {
      method: 'POST',
      corpo: { caminho },
    })
      .then((r) => {
        if (vivo) setUrl(`/api/arquivos/fluxo/${r.token}`);
      })
      .catch(() => {
        if (vivo) setErro('Não foi possível abrir este arquivo.');
      });
    return () => {
      vivo = false;
    };
  }, [maquinaId, caminho, tentativa]);

  if (erro) {
    return (
      <div className="text-sm text-amber-300">
        {erro}{' '}
        <button onClick={() => setTentativa((n) => n + 1)} className="underline">
          tentar de novo
        </button>
      </div>
    );
  }
  if (!url) return <p className="text-sm text-zinc-500">Abrindo…</p>;

  if (especie === 'imagem') {
    return (
      <img src={url} alt={caminho} className="max-h-[28rem] rounded-md object-contain" />
    );
  }
  if (especie === 'audio') {
    return <audio src={url} controls preload="metadata" className="w-full" />;
  }
  if (especie === 'video') {
    return <video src={url} controls preload="metadata" className="max-h-[28rem] w-full" />;
  }
  return <PreviewPdf url={url} />;
}

/**
 * PDF desenhado em canvas pelo pdf.js.
 *
 * Não usa `<embed>` nem `<iframe>`: o leitor do navegador ignoraria a CSP da
 * página e abriria o arquivo com os privilégios dele. Aqui o PDF é só bytes
 * que viram pixels.
 */
function PreviewPdf({ url }: { url: string }): React.JSX.Element {
  const area = useRef<HTMLDivElement>(null);
  const [paginas, setPaginas] = useState(0);
  const [erro, setErro] = useState('');

  useEffect(() => {
    let cancelado = false;
    // Quem se destrói é a tarefa de carregamento, não o documento: fechar a
    // aba no meio de um PDF grande precisa parar o download também.
    let tarefa: { destroy: () => Promise<void> } | null = null;

    void (async () => {
      try {
        const pdfjs = await import('pdfjs-dist');
        pdfjs.GlobalWorkerOptions.workerSrc = new URL(
          'pdfjs-dist/build/pdf.worker.min.mjs',
          import.meta.url,
        ).toString();

        const carregamento = pdfjs.getDocument({ url });
        tarefa = carregamento;
        const doc = await carregamento.promise;
        if (cancelado) return;
        setPaginas(doc.numPages);

        const alvo = area.current;
        if (!alvo) return;
        alvo.replaceChildren();

        // Até dez páginas: um processo de 400 páginas travaria a aba, e quem
        // precisa da página 300 abre o arquivo na máquina.
        const quantas = Math.min(doc.numPages, 10);
        for (let n = 1; n <= quantas; n += 1) {
          const pagina = await doc.getPage(n);
          if (cancelado) return;
          const escala = pagina.getViewport({ scale: 1 });
          const largura = Math.min(alvo.clientWidth || 700, 900);
          const vista = pagina.getViewport({ scale: largura / escala.width });

          const tela = document.createElement('canvas');
          tela.width = Math.floor(vista.width);
          tela.height = Math.floor(vista.height);
          tela.className = 'mb-3 w-full rounded-md bg-white';
          alvo.append(tela);

          const pincel = tela.getContext('2d');
          if (!pincel) continue;
          await pagina.render({ canvas: tela, canvasContext: pincel, viewport: vista }).promise;
        }
      } catch {
        if (!cancelado) setErro('Não consegui desenhar este PDF.');
      }
    })();

    return () => {
      cancelado = true;
      void tarefa?.destroy();
    };
  }, [url]);

  if (erro) return <p className="text-sm text-amber-300">{erro}</p>;
  return (
    <div>
      {paginas > 10 ? (
        <p className="mb-2 text-xs text-zinc-500">
          Mostrando as 10 primeiras de {paginas} páginas.
        </p>
      ) : null}
      <div ref={area} className="max-h-[28rem] overflow-auto" />
    </div>
  );
}
